import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Smartphone, Zap, Wifi, Trophy } from "lucide-react";
import { usePWA } from "@/hooks/usePWA";
import { useNavigate } from "react-router-dom";
import { trackPWAEvent } from "@/utils/pwaAnalytics";
import { useEffect } from "react";

interface FirstLoginInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FirstLoginInstallModal({ isOpen, onClose }: FirstLoginInstallModalProps) {
  const { canInstall, isInstalled, promptInstall } = usePWA();
  const navigate = useNavigate();

  // Track modal opened
  useEffect(() => {
    if (isOpen) {
      trackPWAEvent('modal_opened', 'first_login', canInstall, isInstalled);
    }
  }, [isOpen, canInstall, isInstalled]);

  // Don't show modal if already installed
  if (isInstalled) return null;

  const handleInstallNow = async () => {
    trackPWAEvent('install_clicked', 'first_login', canInstall, isInstalled);
    
    if (canInstall) {
      const success = await promptInstall();
      if (success) {
        onClose();
      }
    } else {
      // Navigate to install page with platform-specific instructions
      navigate("/install");
      onClose();
    }
  };

  const handleMaybeLater = () => {
    trackPWAEvent('modal_dismissed', 'first_login', canInstall, isInstalled);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" aria-describedby="first-login-install-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Smartphone className="h-6 w-6 text-primary" />
            Install Wormble for the Best Experience
          </DialogTitle>
          <DialogDescription id="first-login-install-description">
            Get instant access and play offline with our Progressive Web App
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Benefits Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col items-center text-center p-3 bg-primary/5 rounded-lg">
              <Zap className="h-5 w-5 text-primary mb-1" />
              <p className="text-xs font-medium">Instant Loading</p>
              <p className="text-[10px] text-muted-foreground">~1 second launch</p>
            </div>
            <div className="flex flex-col items-center text-center p-3 bg-primary/5 rounded-lg">
              <Wifi className="h-5 w-5 text-primary mb-1" />
              <p className="text-xs font-medium">Works Offline</p>
              <p className="text-[10px] text-muted-foreground">Play anywhere</p>
            </div>
            <div className="flex flex-col items-center text-center p-3 bg-primary/5 rounded-lg">
              <Smartphone className="h-5 w-5 text-primary mb-1" />
              <p className="text-xs font-medium">Home Screen</p>
              <p className="text-[10px] text-muted-foreground">Like a native app</p>
            </div>
            <div className="flex flex-col items-center text-center p-3 bg-primary/5 rounded-lg">
              <Trophy className="h-5 w-5 text-primary mb-1" />
              <p className="text-xs font-medium">Streak Safe</p>
              <p className="text-[10px] text-muted-foreground">Never miss a day</p>
            </div>
          </div>

          {/* Quick Install Info */}
          <div className="bg-muted p-3 rounded-md text-sm space-y-1">
            <p className="font-medium">Quick Install (~30 seconds):</p>
            <p className="text-xs text-muted-foreground">
              {canInstall ? (
                "Click Install Now and follow the prompt"
              ) : (
                <>
                  <strong>iPhone:</strong> Tap Share → Add to Home Screen<br />
                  <strong>Android:</strong> Tap menu → Install App
                </>
              )}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <Button onClick={handleInstallNow} className="w-full" size="lg">
              {canInstall ? "Install Now" : "View Installation Guide"}
            </Button>
            <Button onClick={handleMaybeLater} variant="ghost" className="w-full">
              Maybe Later
            </Button>
          </div>

          {/* Trust Signal */}
          <p className="text-xs text-center text-muted-foreground">
            Used by thousands of players • No app store needed • ~2MB storage
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
